import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../components/ui/tooltip";
import { toast } from "sonner";
import {
  RefreshCw, Loader2, Search, Users, ArrowDown, ArrowUp,
  AlertTriangle, ExternalLink, CheckCircle2, Info, Download,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { intFmt, usd } from "../lib/format";

const HORIZONS = [
  { value: "lifetime", label: "Lifetime" },
  { value: "365d", label: "Last 365 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "30d", label: "Last 30 days" },
];

const STATUSES = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "trial", label: "Trial" },
  { value: "lapsed", label: "Lapsed" },
  { value: "never", label: "Never" },
];

// Table column definitions. `key` = the field on each row, `sortField` = the
// backend `sort` value (null = not sortable). Order here is the RENDER order
// for both the header and body cells — keep them symmetrical or the columns
// will re-appear misaligned.
const COLUMNS = [
  {
    key: "email",
    sortField: "email",
    label: "Email",
    align: "left",
    tip: "Lowercased email — the join key across Shopify & MyID.",
  },
  {
    key: "true_ltv_usd",
    sortField: "true_ltv_usd",
    label: "True LTV",
    align: "right",
    tip: "True LTV = Shopify revenue (net of refunds) + Subscription revenue (paid MyID+ upgrade_requests).",
  },
  {
    key: "total_shopify_revenue_usd",
    sortField: "total_shopify_revenue_usd",
    label: "Shopify rev",
    align: "right",
    tip: "Sum of net_usd across all this email's Shopify orders. net_usd = gross - refunds, converted to USD at cached FX rates.",
  },
  {
    key: "subscription_revenue_usd",
    sortField: "subscription_revenue_usd",
    label: "Sub rev",
    align: "right",
    tip: "Sum of `amount` on MyID+ upgrade_requests where request_status='processed' (a successful charge), joined via user_id → email. WEB / STRIPE ONLY — Apple & Google Play subscribers show $0 here because per-user Apple/Google revenue isn't attributable from their aggregated Yearly reports (platform limitation).",
  },
  {
    key: "total_shopify_orders",
    sortField: "total_shopify_orders",
    label: "# orders",
    align: "right",
    tip: "Count of non-cancelled Shopify orders for this email (matches the count you see on the Shopify customer profile).",
  },
  {
    key: "first_order_date",
    sortField: "first_order_date",
    label: "First order",
    align: "right",
    tip: "Date of this email's earliest Shopify order (created_at). Blank if the customer has never purchased.",
  },
  {
    key: "active_subscription_status",
    sortField: null,
    label: "Status",
    align: "left",
    tip: "active = paying today · trial = on a MyID+ trial · lapsed = ever activated, not paying now · never = never activated.",
  },
];

const formatPctish = (v) => (v == null ? "—" : `${v.toFixed(1)}%`);
const formatRatio = (v) => (v == null ? "—" : `${v.toFixed(2)}×`);

function Stat({ label, value, hint, tip, testId, tone = "default" }) {
  const accent = {
    default: "text-foreground",
    positive: "text-emerald-600 dark:text-emerald-400",
    warning: "text-amber-600 dark:text-amber-400",
  }[tone];
  return (
    <Card data-testid={testId} className="overflow-hidden">
      <CardContent className="pt-5 pb-4 space-y-1.5">
        <div className="flex items-center gap-1.5">
          <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-medium">
            {label}
          </p>
          {tip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`${label} formula`}
                  data-testid={`${testId}-tip`}
                  className="text-muted-foreground/60 hover:text-foreground transition-colors"
                >
                  <Info className="h-3 w-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="max-w-[280px] leading-snug"
              >
                {tip}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <p className={`text-2xl md:text-3xl font-display font-semibold num ${accent}`}>
          {value}
        </p>
        {hint && (
          <p className="text-xs text-muted-foreground">{hint}</p>
        )}
      </CardContent>
    </Card>
  );
}

function ActiveStatusBadge({ status }) {
  const map = {
    active: { label: "Active", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" },
    trial: { label: "Trial", cls: "bg-sky-500/10 text-sky-700 dark:text-sky-400" },
    lapsed: { label: "Lapsed", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-400" },
    never: { label: "Never", cls: "bg-muted text-muted-foreground" },
  };
  const v = map[status] || map.never;
  return (
    <Badge variant="secondary" className={v.cls}>
      {v.label}
    </Badge>
  );
}

function UnmatchedPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchIt = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers/unmatched");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchIt(); }, [fetchIt]);

  if (loading) return <Skeleton className="h-48 w-full" />;
  if (!data) return null;

  return (
    <Card data-testid="unmatched-panel">
      <CardHeader className="pb-3 flex flex-row items-start gap-2 justify-between">
        <div>
          <CardTitle className="text-base font-display flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Email-join gaps
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Customers visible in one system but missing from the other. If a real customer should be matched but isn't, they probably used a different email on each side.
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-md border border-border/70 p-3" data-testid="unmatched-shopify-no-myid">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Shopify only</p>
            <p className="text-xl font-display font-semibold num">{intFmt(data.counts.shopify_no_myid)}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">bought but never signed up</p>
          </div>
          <div className="rounded-md border border-border/70 p-3" data-testid="unmatched-myid-no-shopify">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">MyID only</p>
            <p className="text-xl font-display font-semibold num">{intFmt(data.counts.myid_no_shopify)}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">subscribed but no Shopify order</p>
          </div>
          <div className="rounded-md border border-border/70 p-3 bg-emerald-500/5" data-testid="unmatched-matched">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Matched</p>
            <p className="text-xl font-display font-semibold num text-emerald-600 dark:text-emerald-400">
              {intFmt(data.counts.matched)}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">both systems on same email</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CustomerTable({ horizon }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("true_ltv_usd");
  const [direction, setDirection] = useState("desc");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const limit = 50;

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit, sort, direction, horizon };
      if (q.trim()) params.q = q.trim().toLowerCase();
      if (status) params.status = status;
      const { data } = await api.get("/customers", { params });
      setRows(data.rows);
      setTotal(data.total);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, status, sort, direction, page, horizon]);

  const handleExportCsv = useCallback(async () => {
    // Downloads the CURRENT filtered / sorted view as a CSV. Streamed on the
    // server, buffered as a Blob here, then handed to the browser via a
    // synthetic <a download> so users get a native "Save file" prompt.
    setExporting(true);
    try {
      const params = { sort, direction, horizon };
      if (q.trim()) params.q = q.trim().toLowerCase();
      if (status) params.status = status;
      const res = await api.get("/customers/export.csv", {
        params,
        responseType: "blob",
      });
      // Try to honor the server-supplied filename; fall back to a stamp.
      const disp = res.headers?.["content-disposition"] || "";
      const m = disp.match(/filename="?([^"]+)"?/);
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      const filename = m ? m[1] : `myid-customers-${stamp}.csv`;
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: "text/csv;charset=utf-8;",
      }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Give the browser a beat to start the download before releasing the
      // blob URL — some browsers cancel the download if the object URL is
      // revoked synchronously.
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
      toast.success(`Exported ${intFmt(total)} customers to ${filename}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e));
    } finally {
      setExporting(false);
    }
  }, [q, status, sort, direction, total, horizon]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { setPage(1); }, [q, status, sort, direction, horizon]);

  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <Card data-testid="customers-table-card">
      <CardHeader className="pb-3 flex flex-col gap-3">
        <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
          <CardTitle className="text-base font-display flex items-center gap-2">
            <Users className="h-4 w-4" />
            Unified customer records · <span className="text-muted-foreground font-normal text-sm">{intFmt(total)}</span>
          </CardTitle>
          <div className="flex gap-2 items-center">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search email…"
                className="pl-8 w-56 h-9 text-sm"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="customers-search-input"
              />
            </div>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="customers-status-select"
            >
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <Button
              variant="outline"
              size="sm"
              className="h-9 gap-1.5"
              disabled={exporting || loading || total === 0}
              onClick={handleExportCsv}
              data-testid="customers-export-csv-btn"
              title="Download the current filtered/sorted view as a CSV file"
            >
              {exporting ?
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
                <Download className="h-3.5 w-3.5" />}
              <span className="text-xs">Export CSV</span>
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border border-border/70 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {COLUMNS.map((col) => {
                  const sortable = col.sortField !== null;
                  const active = sortable && sort === col.sortField;
                  return (
                    <TableHead
                      key={col.key}
                      className={
                        (col.align === "right" ? "text-right " : "") +
                        (sortable ? "cursor-pointer" : "")
                      }
                      onClick={() => {
                        if (!sortable) return;
                        if (active) {
                          setDirection((d) => (d === "asc" ? "desc" : "asc"));
                        } else {
                          setSort(col.sortField);
                          setDirection("desc");
                        }
                      }}
                      data-testid={`customers-header-${col.key}`}
                    >
                      <span
                        className={
                          "inline-flex items-center gap-1 " +
                          (col.align === "right" ? "flex-row-reverse" : "")
                        }
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="inline-flex items-center gap-1 group"
                              data-testid={`customers-header-label-${col.key}`}
                            >
                              {col.label}
                              <Info className="h-3 w-3 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className="max-w-[300px] leading-snug"
                          >
                            {col.tip}
                          </TooltipContent>
                        </Tooltip>
                        {active && (direction === "asc" ?
                          <ArrowUp className="h-3 w-3" /> :
                          <ArrowDown className="h-3 w-3" />)}
                      </span>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && rows.length === 0 ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {COLUMNS.map((col) => (
                      <TableCell key={col.key}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    className="text-center text-muted-foreground py-10 text-sm"
                  >
                    No customers yet. Connect Shopify in Settings and run a sync.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.email} data-testid={`customer-row-${r.email}`}>
                    {COLUMNS.map((col) => {
                      const alignCls =
                        col.align === "right" ? "text-right num" : "";
                      let content;
                      if (col.key === "email") {
                        content = (
                          <span className="font-mono text-xs truncate max-w-[260px] block">
                            {r.email}
                          </span>
                        );
                      } else if (col.key === "first_order_date") {
                        content = r.first_order_date
                          ? r.first_order_date.slice(0, 10)
                          : "—";
                      } else if (col.key === "total_shopify_orders") {
                        content = intFmt(r.total_shopify_orders);
                      } else if (col.key === "active_subscription_status") {
                        content = (
                          <ActiveStatusBadge
                            status={r.active_subscription_status}
                          />
                        );
                      } else {
                        // dollar columns
                        content = usd(r[col.key]);
                      }
                      return (
                        <TableCell
                          key={col.key}
                          className={alignCls}
                          data-testid={`customer-cell-${col.key}-${r.email}`}
                        >
                          {content}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
          <span>Page {page} of {pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading}
              onClick={() => setPage((p) => p - 1)} data-testid="customers-page-prev">Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= pages || loading}
              onClick={() => setPage((p) => p + 1)} data-testid="customers-page-next">Next</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Customers() {
  const [horizon, setHorizon] = useState("lifetime");
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers/metrics", { params: { horizon } });
      setMetrics(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [horizon]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const handleRebuild = async () => {
    setRebuilding(true);
    try {
      await api.post("/customers/rebuild");
      toast.info("Rebuild started — this can take a minute on large datasets.");
      // Poll status until success/error. Cloudflare's edge timeout is
      // ~100s so we can't just await the POST — the rebuild runs in a
      // background task on the server and we watch sync_state here.
      const start = Date.now();
      const MAX_MS = 10 * 60 * 1000; // 10 min ceiling
      while (Date.now() - start < MAX_MS) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: s } = await api.get("/customers/rebuild/status");
        if (s.status === "success") {
          const r = s.result || {};
          toast.success(
            `Rebuilt — ${intFmt(r.total_emails || 0)} customers (${s.duration_s || "?"}s)`,
          );
          fetchMetrics();
          return;
        }
        if (s.status === "error") {
          toast.error(s.message || "Rebuild failed");
          return;
        }
      }
      toast.warning(
        "Rebuild is still running after 10 minutes — check back later.",
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRebuilding(false);
    }
  };

  const m = metrics?.metrics || {};
  const c = metrics?.counts || {};

  return (
    <AppShell>
      <TooltipProvider delayDuration={150}>
      <div className="p-4 md:p-8 space-y-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight">Customers & LTV</h1>
            <p className="text-sm text-muted-foreground mt-1">
              One row per email — combining Shopify purchases with MyID+ subscription lifecycle.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Tabs value={horizon} onValueChange={setHorizon} className="w-auto">
              <TabsList data-testid="customers-horizon-tabs">
                {HORIZONS.map((h) => (
                  <TabsTrigger
                    key={h.value}
                    value={h.value}
                    data-testid={`customers-horizon-${h.value}`}
                    className="text-xs md:text-sm"
                  >
                    {h.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRebuild}
              disabled={rebuilding}
              data-testid="customers-rebuild-btn"
            >
              {rebuilding ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Rebuild from raw data
            </Button>
          </div>
        </div>

        {metrics?.refreshed_at && (
          <p className="text-[11px] text-muted-foreground">
            Last rebuilt: <span className="num">{new Date(metrics.refreshed_at).toLocaleString()}</span>
            {" · "}Records: <span className="num">{intFmt(c.shopify_purchasers + c.subscribers_no_shopify)}</span>
          </p>
        )}

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat
              label="Sub attach rate"
              value={formatPctish(m.attach_rate)}
              hint={`${intFmt(c.shopify_purchasers_activated)} / ${intFmt(c.shopify_purchasers)} buyers`}
              tip="Shopify buyers who ever activated MyID+ ÷ total Shopify buyers. Shopify buyer = at least one non-cancelled Shopify order. Activated = at least one MyID+ subscription in any status (matched by email)."
              testId="kpi-attach-rate"
              tone="positive"
            />
            <Stat
              label="True customer LTV"
              value={usd(m.true_customer_ltv_usd)}
              hint="Shopify + sub revenue per buyer"
              tip="Σ (Shopify revenue + MyID+ subscription revenue) across all Shopify buyers ÷ number of Shopify buyers. Includes both subscribers and non-subscribers."
              testId="kpi-true-ltv"
            />
            <Stat
              label="Subscriber LTV"
              value={usd(m.subscriber_ltv_usd)}
              hint="Buyers who activated MyID+"
              tip="Σ True LTV across Shopify buyers who activated MyID+ ÷ count of those buyers."
              testId="kpi-subscriber-ltv"
              tone="positive"
            />
            <Stat
              label="Non-subscriber LTV"
              value={usd(m.non_subscriber_ltv_usd)}
              hint="Buyers who never activated"
              tip="Σ True LTV across Shopify buyers who never activated MyID+ ÷ count of those buyers."
              testId="kpi-nonsubscriber-ltv"
            />
            <Stat
              label="LTV uplift"
              value={formatRatio(m.ltv_uplift_ratio)}
              hint="Subscriber ÷ non-subscriber"
              tip="Subscriber LTV ÷ Non-subscriber LTV. > 1× means MyID+ subscribers are worth more than non-subscribers on average."
              testId="kpi-ltv-uplift"
              tone={m.ltv_uplift_ratio && m.ltv_uplift_ratio > 1.5 ? "positive" : "default"}
            />
          </div>
        )}

        <UnmatchedPanel />

        <CustomerTable horizon={horizon} />
      </div>
      </TooltipProvider>
    </AppShell>
  );
}
